<template>
  <div>
    <div class="money" :class="moneyClass">
      <label v-if="!hideLabel" class="money-label" :for="`moneyInput${id}`" :class="labelClass">
        {{ label }}
      </label>

      <t-input
        :id="`moneyInput${id}`"
        ref="initialCurrency"
        class="money-input"
        :placeholder="placeholder"
        :value="money.initialCurrency.sum"
        :data-cy="`${dataCy}-base-currency`"
        :disabled="disabled"
        inputmode="decimal"
        @keyup="onKeyupInput"
        @blur="onBlur"
      />

      <div v-if="isShownRightSlot" class="money-slot-wrapper">
        <slot name="right" />
      </div>
    </div>

    <div v-if="error" class="error-message" :data-cy="`${dataCy}-error-message`">
      {{ error }}
    </div>
  </div>
</template>

<script>
import Cleave from "cleave.js";
import { getRandomId } from "@/services/_bit/UiService";
import MoneyService, { SYMBOL_MINUS, DOUBLE_ZERO } from "@/services/_bit/MoneyService";
import TInput from "vue-tailwind/dist/t-input";

export default {
  components: {
    TInput,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },

    cleaveSettings: {
      type: Object,
      default: () => ({}),
    },

    hideLabel: {
      type: Boolean,
      default: false,
    },

    id: {
      type: String,
      default: () => getRandomId(),
    },

    error: {
      type: String,
      default: "",
    },

    numeralDecimalScale: {
      type: Number,
      default: 2,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    withMinus: {
      type: Boolean,
      default: false,
    },

    i18n: {
      type: Object,
      default: () => ({}),
    },

    placeholder: {
      type: String,
      default: "",
    },

    dataCy: {
      type: String,
      default: "",
    },
  },

  data: () => ({
    money: {
      initialCurrency: {
        symbol: "",
        sum: "",
        label: "",
      },
    },
  }),

  computed: {
    isShownRightSlot() {
      return !!this.$slots["right"];
    },

    moneyClass() {
      return {
        "error-input": this.error,
        disabled: this.disabled,
      };
    },

    labelClass() {
      return {
        "error-label": this.error,
      };
    },

    label() {
      const comma = this.money?.initialCurrency?.symbol ? "," : "";

      return `${this.money?.initialCurrency?.label}${comma} ${this.money?.initialCurrency?.symbol}`;
    },

    isShownMinus() {
      return this.withMinus ? SYMBOL_MINUS : "";
    },

    moneyInput() {
      return this.$el.querySelector(`#moneyInput${this.id}`);
    },
  },

  watch: {
    money: {
      handler: "onChangeMoney",
      deep: true,
    },
    value: {
      handler: "onChangeValue",
      deep: true,
    },
    withMinus: "onChangeWithMinus",
  },

  created() {
    this.prefillInput();
  },

  mounted() {
    this.cleaveMoneyInput = new Cleave(this.moneyInput, {
      ...this.cleaveSettings,
      numeralDecimalScale: this.numeralDecimalScale,
      prefix: this.isShownMinus,
    });

    this.moneyInput.addEventListener("paste", (event) => this.onPaste(event));
    this.moneyInput.addEventListener("keydown", (event) => this.onKeydown(event));

    this.money.initialCurrency.sum = this.cleaveMoneyInput.getFormattedValue();
  },

  beforeDestroy() {
    this.moneyInput.removeEventListener("paste", (event) => this.onPaste(event));
    this.moneyInput.removeEventListener("keydown", (event) => this.onKeydown(event));
  },

  methods: {
    onPaste(event) {
      event.preventDefault();
      event.target.value = (event.clipboardData || window.clipboardData)
        .getData("text")
        .replace(".", ",");
    },

    onKeydown(event) {
      if (event.key === ".") {
        event.preventDefault();
        event.target.value += ",";
      }
    },

    onChangeWithMinus() {
      this.cleaveMoneyInput = new Cleave(this.moneyInput, {
        ...this.cleaveSettings,
        numeralDecimalScale: this.numeralDecimalScale,
        prefix: this.isShownMinus,
      });

      this.money.initialCurrency.sum = this.cleaveMoneyInput.getFormattedValue();
    },

    prefillInput() {
      const separatedSum = new MoneyService().separatedMoney(
        Number(this.value?.initialCurrency?.sum)
      );

      const sum =
        separatedSum.penny === DOUBLE_ZERO
          ? separatedSum.integer
          : `${separatedSum.integer}${separatedSum.delimiter}${separatedSum.penny}`;

      this.money.initialCurrency = {
        symbol: this.value?.initialCurrency?.symbol || "",
        sum: Number(sum) === 0 ? "" : sum,
        placeholder: this.value?.initialCurrency?.placeholder,
        label: this.value?.initialCurrency?.label || this.i18n.amount,
      };
    },

    onChangeMoney() {
      let preparedMoney = {
        initialCurrency: {
          label: this.money.initialCurrency.label,
          symbol: this.money.initialCurrency.symbol,
          placeholder: this.money.initialCurrency.placeholder,
          sum: this.cleaveMoneyInput.getRawValue(),
        },
      };

      if (this.value?.convertibleCurrency) {
        preparedMoney.convertibleCurrency = {
          symbol: this.value?.convertibleCurrency?.symbol || "",
          sum: this.value?.convertibleCurrency?.sum || "",
          placeholder: this.value?.convertibleCurrency?.placeholder || "",
          label: this.value?.convertibleCurrency?.label || "",
        };
      }

      if (this.value?.firstRate) {
        preparedMoney.firstRate = {
          symbol: this.value?.firstRate?.symbol || "",
          sum: this.value?.firstRate?.sum || "",
          placeholder: this.value?.firstRate?.placeholder || "",
          label: this.value?.firstRate?.label || "",
        };
      }

      if (this.value?.secondRate) {
        preparedMoney.secondRate = {
          symbol: this.value?.secondRate?.symbol || "",
          sum: this.value?.secondRate?.sum || "",
          placeholder: this.value?.secondRate?.placeholder || "",
          label: this.value?.secondRate?.label || "",
        };
      }

      this.$emit("input", preparedMoney);
    },

    onChangeValue(value) {
      this.cleaveMoneyInput.setRawValue(value.initialCurrency.sum);
      this.money.initialCurrency.sum = this.cleaveMoneyInput.getFormattedValue();
      this.money.initialCurrency.symbol = value.initialCurrency.symbol;
    },

    onKeyupInput() {
      this.money.initialCurrency.sum = this.cleaveMoneyInput.getFormattedValue();

      this.$nextTick(() => {
        this.$emit("keyup");
      });
    },

    onBlur() {
      this.$nextTick(() => {
        this.$emit("blur");
      });
    },
  },
};
</script>

<style lang="postcss" scoped>
.money {
  @apply flex;
  @apply rounded-lg border border-gray-300 bg-white;
  @apply px-4 py-2.5;
  @apply transition-all duration-100;
  @apply relative;

  &:hover {
    @apply border-gray-400;
  }

  &:focus-within {
    @apply border-gray-500 ring-4 ring-gray-600 ring-opacity-15;
  }

  &-label {
    @apply absolute block;
    @apply whitespace-nowrap text-sm font-normal text-gray-500;
    @apply pb-0.5;
  }

  &-input {
    @apply text-base font-normal text-gray-900;
    @apply rounded-none border-0 shadow-none ring-0;
    @apply mt-[1.125rem] flex-auto py-0 pl-0 pr-4;

    &:focus {
      @apply ring-0;
    }
  }

  &-slot-wrapper {
    @apply flex w-full flex-1 items-center whitespace-nowrap;
  }
}

.error-message {
  @apply text-xs font-normal text-red-500;
  @apply mt-2 pl-4;
}

.error-label {
  @apply text-red-500;
}

.error-input {
  @apply border-red-300;

  &:hover {
    @apply border-red-400;
  }

  &:focus-within {
    @apply border-red-500 ring-red-100;
  }
}

.disabled {
  @apply bg-gray-100;
  @apply cursor-not-allowed;

  &:deep(.money-input) {
    @apply bg-gray-100;
    @apply text-gray-900 opacity-100;
  }
}
</style>
